/**
 * Pure nav-grouping logic, split out of AppLayout.tsx so it can be unit
 * tested without pulling in AppLayout's full render tree (Header, StatusBar,
 * providers, etc).
 */
import { type NavItemConfig, type NavGroupId } from '@/components/layout/NavItem'
import { type NavGroup } from '@/components/layout/Sidebar'
import { hasAnyPermission } from '@shared/auth/permissions'

/* ── Path-based fallback grouping for legacy feature modules ───────── */
export function inferGroup(item: NavItemConfig): NavGroupId {
  if (item.group) return item.group
  const p = item.path
  if (p.startsWith('/admin')) return 'admin'
  if (p === '/settings' || p.startsWith('/settings')) return 'admin'
  if (p === '/reports' || p.startsWith('/reports')) return 'insights'
  if (p === '/files' || p.startsWith('/files')) return 'insights'
  if (p === '/docs' || p.startsWith('/docs')) return 'insights'
  return 'workspace'
}

const WORKSPACE_ORDER = [
  '/dashboard',
  '/projects',
  '/catalog',
  '/contacts',
  '/inventory',
  '/manufacturer-settings',
]

/** Return the index of the most-specific (longest) matching entry. */
function findOrderIndex(path: string): number {
  let bestIdx = -1
  let bestLen = -1
  WORKSPACE_ORDER.forEach((p, i) => {
    if ((path === p || path.startsWith(p + '/')) && p.length > bestLen) {
      bestLen = p.length
      bestIdx = i
    }
  })
  return bestIdx
}

function sortWorkspace(a: NavItemConfig, b: NavItemConfig): number {
  const ai = findOrderIndex(a.path)
  const bi = findOrderIndex(b.path)
  if (ai === -1 && bi === -1) return 0
  if (ai === -1) return 1
  if (bi === -1) return -1
  return ai - bi
}

/**
 * Single source of truth for whether a nav item is visible to the current
 * user. NavItem uses this to decide whether to render itself; buildGroups
 * uses it to decide whether a group header has any visible item, so the two
 * can never disagree.
 */
export function isItemVisible(item: NavItemConfig, permissions: string[]): boolean {
  const required = item.requiredPermissions ?? item.requiredScopes
  return !required || required.length === 0 || hasAnyPermission(permissions, required)
}

/**
 * A group header must only appear when it has at least one item the current
 * user is actually permitted to see, otherwise the sidebar shows an empty
 * "Admin" header to non-admin users.
 */
export function buildGroups(items: NavItemConfig[], permissions: string[]): NavGroup[] {
  const buckets: Record<NavGroupId, NavItemConfig[]> = {
    workspace: [],
    insights: [],
    admin: [],
  }
  for (const item of items) {
    if (!isItemVisible(item, permissions)) continue
    buckets[inferGroup(item)].push(item)
  }
  buckets.workspace.sort(sortWorkspace)

  const result: NavGroup[] = []
  if (buckets.workspace.length) result.push({ label: 'Workspace', items: buckets.workspace })
  // TODO: unhide when Insights implementation begins
  // if (buckets.insights.length)  result.push({ label: 'Insights',  items: buckets.insights })
  if (buckets.admin.length)     result.push({ label: 'Admin',     items: buckets.admin })
  return result
}
